import { StyleSheet, css } from 'aphrodite';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useUserSession } from '../../../models/hooks/appStateHooks';
import { Button } from '../../../web/components-v2/Button';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../../web/components-v2/Select';
import { FieldKey } from '../../viewModels/form';
import type { IMeme } from '../../viewModels/gamification';
import type { ISkipReason, LeadServedSource } from '../../viewModels/queue';
import { useLeadContacts } from '../data/useLeadContacts';
import { useSkipLead } from '../data/useSkipLead';
import { useSkipReasons } from '../data/useSkipReasons';
import { DealForm } from '../deal';
import { CCUserPicker } from './CCUserPicker';
import { FollowUpSelect } from './FollowUpSelect';
import { MemeOverlay } from './MemeOverlay';
import { NoteEditor } from './NoteEditor';
import { OutcomePicker } from './OutcomePicker';
import { SkipReasonDialog } from './SkipReasonDialog';
import type { PhoneCallOutcome } from './types';
import { useFollowUpOptions } from './useFollowUpOptions';
import { useOutcomes } from './useOutcomes';
import { useWrapup } from './useWrapup';

export type WrapupPanelProps = {
	/** Company id for the lead being wrapped up. */
	companyId: string | undefined;
	/** Phone call id from the dialer. Pass `'manual'` for the manual-log path. */
	phoneCallId: string | undefined;
	/** Source attribution for the save call. */
	source: LeadServedSource;
	/** Lead/contact context for the panel header (display only). */
	leadName?: string;
	contactName?: string;
	/**
	 * Most-recent deal id for the lead, when present. When the rep picks
	 * `NewDemo` or `AppointmentRescheduled` the deal-form step mounts in
	 * Update mode against this id — mirrors legacy
	 * `lead.loadDealForm(lastDeal.id)` (`QueueNoteEditor/index.tsx:336`).
	 * When undefined the deal-form falls back to Create mode for those
	 * outcomes (matches behaviour when the lead has no prior deal).
	 */
	lastDealId?: string;
	/** Fired when a non-Booked submit succeeds (drawer closes, parent advances). */
	onSubmitted?: () => void;
	/** Fired when submit indicates a deal form is needed (Booked-class outcomes). */
	onRequireDealForm?: () => void;
};

const DM_NONE = '__none__';
const DM_OTHER = '__other__';

/**
 * Drawer-tab "Wrap-up" panel. Composes the wrap-up workflow:
 *
 *   pick-outcome → (pick-child-outcome | note) → submit
 *
 * Booked-class outcomes short-circuit submit and fire `onRequireDealForm`
 * so the parent can switch the drawer to the deal form. Non-Booked outcomes
 * route through useSaveCallOutcome and fire `onSubmitted` on success.
 *
 * The "Manually Log Call" button appears at the picker step (top-level only,
 * NOT during the drill-down). Clicking it flips the panel into manual mode —
 * `phoneCallId` is overridden to `'manual'` for the rest of the wrap-up so
 * the save call routes through the lead outcome endpoint instead of the
 * dialer's phonecall endpoint.
 *
 * Above the note editor, a small "Connected With:" decision-maker dropdown
 * and an optional "Add CC" picker mirror the legacy
 * `aida/components/AidaNoteEditor`. The decision-maker selector is hidden for
 * the Levitate Sales Coffee account (matches the legacy gate at
 * `AidaNoteEditor/index.tsx:97`).
 *
 * On a successful save (or deal create), the gamification `IMeme` returned
 * by `useSaveCallOutcome` / `useCreateDeal` is rendered through the local
 * `MemeOverlay`. The overlay auto-dismisses after ~3s.
 */
export const WrapupPanel: FC<WrapupPanelProps> = ({
	companyId,
	phoneCallId,
	source,
	leadName,
	contactName,
	lastDealId,
	onSubmitted,
	onRequireDealForm,
}) => {
	const userSession = useUserSession();
	const { outcomeShortcuts, connectedGroup, badLeadOutcome, isLoading } = useOutcomes(companyId);
	const [manualLogActive, setManualLogActive] = useState(false);
	const [activeMeme, setActiveMeme] = useState<IMeme | null>(null);
	const effectivePhoneCallId = manualLogActive ? 'manual' : phoneCallId;
	const wrapup = useWrapup({
		companyId,
		phoneCallId: effectivePhoneCallId,
		source,
		connectedGroup,
		badLeadOutcome,
		onSubmitted,
	});

	// Skip-reason picker state. Opens whenever the rep clicks BadLead from the
	// outcome picker (top-level OR drill-down) — so wrap-up skip behaves the
	// same as the TopBar "Skip lead" entry-point: pick a specific reason from
	// `skipReasons` / `tempSkipReasons` instead of the legacy single-click
	// "BadLead → note → save & skip" path.
	const [skipDialogOpen, setSkipDialogOpen] = useState(false);
	const skipReasonsResult = useSkipReasons(skipDialogOpen ? companyId : undefined);
	const skipLeadMutation = useSkipLead();
	const [pendingSkipReasonId, setPendingSkipReasonId] = useState<string | null>(null);

	const isBadLeadOutcome = useCallback(
		(outcome: PhoneCallOutcome): boolean => {
			if (outcome.name === FieldKey.BadLead) return true;
			if (badLeadOutcome && outcome.id === badLeadOutcome.id) return true;
			return false;
		},
		[badLeadOutcome]
	);

	const handlePickOutcome = useCallback(
		(outcome: PhoneCallOutcome) => {
			if (isBadLeadOutcome(outcome)) {
				setSkipDialogOpen(true);
				return;
			}
			wrapup.pickOutcome(outcome);
		},
		[isBadLeadOutcome, wrapup]
	);

	const handlePickChild = useCallback(
		(child: PhoneCallOutcome) => {
			if (isBadLeadOutcome(child)) {
				setSkipDialogOpen(true);
				return;
			}
			wrapup.pickChild(child);
		},
		[isBadLeadOutcome, wrapup]
	);

	const handlePickSkipReason = useCallback(
		(reason: ISkipReason) => {
			if (!companyId) return;
			setPendingSkipReasonId(reason.id);
			skipLeadMutation.mutate(
				{
					companyId,
					followUp: null,
					currentCallId: effectivePhoneCallId ?? 'manual',
					outcomeId: reason.id,
					skipReason: reason.name,
					leadServedSource: source,
				},
				{
					onSuccess: () => {
						setPendingSkipReasonId(null);
						setSkipDialogOpen(false);
						toast.success('Lead skipped');
						// Drawer + advance handled by the parent via `onSubmitted` —
						// same forward path as a non-Booked outcome save.
						onSubmitted?.();
					},
					onError: err => {
						setPendingSkipReasonId(null);
						toast.error('Failed to skip lead');
						console.error('Skip lead error:', err);
					},
				}
			);
		},
		[companyId, skipLeadMutation, effectivePhoneCallId, source, onSubmitted]
	);

	// Whether the deal-form surface is showing. The state machine drives this
	// via `step === 'deal-form'` for the Booked-class pick path; the legacy
	// submit-result `requiresDealForm` path is kept as a defensive escape
	// hatch (in practice nothing reaches submit with a Booked outcome any
	// more — the pick handlers transition straight to the deal-form step).
	const isDealFormStep = wrapup.state.step === 'deal-form';

	// Per-outcome follow-up options. Empty until the rep picks a leaf outcome
	// (companyId + outcomeId both required to enable the query); the
	// `<FollowUpSelect>` falls back to the static curated list while empty.
	const followUpOptionsResult = useFollowUpOptions(companyId, wrapup.state.selectedOutcome?.id);
	const dynamicFollowUpOptions = followUpOptionsResult.options;

	// Contacts list backs the "Connected With:" decision-maker dropdown above
	// the note editor. Hidden for the Levitate Sales Coffee account (matches
	// `AidaNoteEditor/index.tsx:97` gate).
	const contactsQuery = useLeadContacts(companyId);
	const contacts = contactsQuery.data ?? [];
	const showDecisionMakerSelector = !userSession?.account?.isLevitateSalesCoffeeAccount;

	const isManualDial = effectivePhoneCallId === 'manual';

	// Notify the parent once we transition into the deal-form step. Today's
	// parent treats this as a no-op (the panel handles the body switch
	// itself); the callback exists so the parent can ensure the drawer is
	// open / wired up correctly.
	useEffect(() => {
		if (isDealFormStep) {
			onRequireDealForm?.();
		}
	}, [isDealFormStep, onRequireDealForm]);

	const handleSave = async () => {
		const result = await wrapup.submit();
		if (result.meme) {
			setActiveMeme(result.meme);
		}
	};

	// Pre-fill the deal form's note-typed fields with whatever the rep typed
	// before changing their mind to a Booked-class outcome. The legacy
	// `DealDrawerContent` seeds the form's `ExecutiveSummaryNote` and
	// `OutcomeNotes` fields from the dealModal context; we mirror those
	// exact field names here so a server-side schema with either name picks
	// up the wrap-up's draft.
	const dealFormInitialValues = useMemo<Record<string, unknown>>(() => {
		if (!wrapup.state.noteHtml) return {};
		return {
			ExecutiveSummaryNote: wrapup.state.noteHtml,
			OutcomeNotes: wrapup.state.noteHtml,
		};
	}, [wrapup.state.noteHtml]);

	const dealFormCustomProperties = useMemo<Record<string, unknown> | undefined>(() => {
		const props: Record<string, unknown> = {};
		if (wrapup.state.decisionMakerId) {
			props.decisionMaker = wrapup.state.decisionMakerId;
		}
		if (wrapup.state.ccUserIds.length > 0) {
			props.ccUserIds = wrapup.state.ccUserIds;
		}
		return Object.keys(props).length > 0 ? props : undefined;
	}, [wrapup.state.decisionMakerId, wrapup.state.ccUserIds]);

	const subtitleParts = [leadName, contactName].filter((p): p is string => Boolean(p));
	const subtitle = subtitleParts.join(' · ');

	// Reschedule outcomes (`NewDemo` / `AppointmentRescheduled`) flip the deal
	// form into Update mode against the lead's most-recent deal — matches the
	// legacy `lead.loadDealForm(lastDeal.id)` call at
	// `QueueNoteEditor/index.tsx:336`. Falls back to Create when the lead has
	// no prior deal (`lastDealId` undefined). The plain `Booked` outcome
	// always means a fresh deal, so we explicitly only key off the two
	// reschedule outcomes here.
	const isRescheduleOutcome =
		wrapup.state.selectedOutcome?.name === FieldKey.NewDemo ||
		wrapup.state.selectedOutcome?.name === FieldKey.AppointmentRescheduled;
	const dealFormDealId = isRescheduleOutcome ? lastDealId : undefined;
	const dealFormHeaderTitle = dealFormDealId ? 'Update Deal' : 'Create Deal';

	if (isDealFormStep) {
		return (
			<>
				<div className={css(styles.root)}>
					<header className={css(styles.head)}>
						<div className={css(styles.headLeft)}>
							<span className={css(styles.title)}>{dealFormHeaderTitle}</span>
							{subtitle && <span className={css(styles.subtitle)}>{subtitle}</span>}
						</div>
					</header>
					<div className={css(styles.body)}>
						{companyId ? (
							<DealForm
								companyId={companyId}
								dealId={dealFormDealId}
								initialValues={dealFormInitialValues}
								customProperties={dealFormCustomProperties}
								onCancel={() => wrapup.back()}
								onCreated={result => {
									if (result.meme) {
										setActiveMeme(result.meme);
									}
									onSubmitted?.();
								}}
							/>
						) : (
							<div className={css(styles.dealFormError)} aria-live='polite'>
								Cannot create a deal without a company id.
							</div>
						)}
					</div>
				</div>
				<MemeOverlay meme={activeMeme} onDismiss={() => setActiveMeme(null)} />
			</>
		);
	}

	const decisionMakerValue = wrapup.state.decisionMakerId ?? DM_NONE;
	const handleDecisionMakerChange = (next: string) => {
		if (next === DM_NONE || next === DM_OTHER) {
			wrapup.setDecisionMaker(null);
			return;
		}
		wrapup.setDecisionMaker(next);
	};

	return (
		<>
			<div className={css(styles.root)}>
				<header className={css(styles.head)}>
					<div className={css(styles.headLeft)}>
						<span className={css(styles.title)}>Call Notes</span>
						{subtitle && <span className={css(styles.subtitle)}>{subtitle}</span>}
					</div>
					{isManualDial && <span className={css(styles.manualBadge)}>Manual Dial</span>}
				</header>

				<div className={css(styles.body)}>
					{(wrapup.state.step === 'pick-outcome' || wrapup.state.step === 'pick-child-outcome') && (
						<OutcomePicker
							mode={wrapup.state.step === 'pick-outcome' ? 'top-level' : 'drill-down'}
							outcomes={outcomeShortcuts}
							selectedGroup={wrapup.state.selectedGroup}
							onPickOutcome={handlePickOutcome}
							onPickChild={handlePickChild}
							onBack={wrapup.back}
							isLoading={isLoading}
							eyebrow={isManualDial ? 'Outcome' : 'Last Call Outcome?'}
						/>
					)}

					{wrapup.state.step === 'note' && (
						<div className={css(styles.noteStep)}>
							<button type='button' className={css(styles.backLink)} onClick={wrapup.back}>
								← Back
							</button>
							<div className={css(styles.outcomeChip)}>
								<span
									className={css(styles.outcomeChipDot)}
									style={{ background: wrapup.state.selectedOutcome?.hexColor ?? 'var(--ink-muted)' }}
								/>
								{wrapup.state.selectedOutcome?.label}
							</div>
							{showDecisionMakerSelector && (
								<label className={css(styles.dmRow)}>
									<span className={css(styles.dmLabel)}>Connected With:</span>
									<Select value={decisionMakerValue} onValueChange={handleDecisionMakerChange}>
										<SelectTrigger styles={styles.dmTrigger}>
											{(() => {
												const selected = contacts.find(c => c.id === wrapup.state.decisionMakerId);
												if (!selected) return 'Other';
												return selected.title ? `${selected.name} — ${selected.title}` : selected.name;
											})()}
										</SelectTrigger>
										<SelectContent>
											<SelectItem value={DM_NONE}>None</SelectItem>
											{contacts.map(c => (
												<SelectItem key={c.id} value={c.id}>
													{c.title ? `${c.name} — ${c.title}` : c.name}
												</SelectItem>
											))}
											<SelectItem value={DM_OTHER}>Other</SelectItem>
										</SelectContent>
									</Select>
								</label>
							)}
							<CCUserPicker value={wrapup.state.ccUserIds} onChange={wrapup.setCcUserIds} />
							<NoteEditor value={wrapup.state.noteHtml} onChange={wrapup.setNote} placeholder='Notes from this call…' />
							{wrapup.saveDisabledReason && <div className={css(styles.error)}>{wrapup.saveDisabledReason}</div>}
							<div className={css(styles.ctaRow)}>
								<FollowUpSelect
									value={wrapup.state.followUp}
									onChange={wrapup.setFollowUp}
									options={dynamicFollowUpOptions.length > 0 ? dynamicFollowUpOptions : undefined}
								/>
								<Button onClick={handleSave} disabled={!wrapup.canSave || wrapup.isSaving}>
									{wrapup.isSaving ? 'Saving…' : wrapup.state.mode === 'skip' ? 'Save & Skip' : 'Save'}
								</Button>
							</div>
						</div>
					)}
				</div>

				{wrapup.state.step === 'pick-outcome' && (
					<footer className={css(styles.footer)}>
						<Button variant='outline' size='sm' onClick={() => setManualLogActive(true)} disabled={manualLogActive}>
							{manualLogActive ? 'Manual call active' : 'Manually Log Call'}
						</Button>
					</footer>
				)}
			</div>
			<MemeOverlay meme={activeMeme} onDismiss={() => setActiveMeme(null)} />
			<SkipReasonDialog
				open={skipDialogOpen}
				onOpenChange={open => {
					if (!open && skipLeadMutation.isPending) return;
					setSkipDialogOpen(open);
				}}
				skipReasons={skipReasonsResult.skipReasons}
				tempSkipReasons={skipReasonsResult.tempSkipReasons}
				loading={skipReasonsResult.isLoading}
				error={skipReasonsResult.error?.message}
				pendingReasonId={pendingSkipReasonId}
				onPick={handlePickSkipReason}
			/>
		</>
	);
};

const styles = StyleSheet.create({
	root: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		background: 'var(--bg-elev)',
	},
	head: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 12,
		padding: '14px 18px',
		borderBottom: '1px solid var(--border)',
		flexShrink: 0,
	},
	headLeft: {
		display: 'flex',
		flexDirection: 'column',
		gap: 2,
	},
	title: {
		fontSize: 13,
		fontWeight: 600,
		color: 'var(--ink)',
	},
	subtitle: {
		fontSize: 11,
		color: 'var(--ink-muted)',
	},
	manualBadge: {
		fontSize: 11,
		fontWeight: 500,
		color: 'var(--ink-muted)',
		background: 'var(--bg)',
		border: '1px solid var(--border)',
		padding: '2px 8px',
		borderRadius: 999,
	},
	body: {
		flex: 1,
		overflowY: 'auto',
		padding: 0,
	},
	noteStep: {
		display: 'flex',
		flexDirection: 'column',
		gap: 12,
		padding: '14px 18px',
		// Fill the drawer's body so the NoteEditor (which is `flex: 1`) gets
		// real vertical space to expand into instead of collapsing to its
		// content's intrinsic height.
		flex: 1,
		minHeight: 0,
	},
	backLink: {
		alignSelf: 'flex-start',
		background: 'transparent',
		border: 'none',
		color: 'var(--accent)',
		cursor: 'pointer',
		fontSize: 12,
		fontWeight: 500,
		padding: 0,
	},
	outcomeChip: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 8,
		alignSelf: 'flex-start',
		padding: '4px 10px',
		background: 'var(--bg)',
		border: '1px solid var(--border)',
		borderRadius: 999,
		fontSize: 12,
		color: 'var(--ink)',
	},
	outcomeChipDot: {
		width: 8,
		height: 8,
		borderRadius: 999,
		display: 'inline-block',
	},
	error: {
		fontSize: 12,
		color: 'var(--accent)',
	},
	ctaRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 12,
	},
	footer: {
		padding: '12px 18px',
		borderTop: '1px solid var(--border)',
	},
	dealFormError: {
		padding: '14px 18px',
		color: 'var(--ink-muted)',
		fontSize: 13,
	},
	dmRow: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
	},
	dmLabel: {
		fontSize: 12,
		fontWeight: 500,
		color: 'var(--ink-muted)',
	},
	dmTrigger: {
		minWidth: 200,
	},
});
